import { Injectable, inject, signal } from '@angular/core';
import { Store } from '@ngrx/store';
import { TranslateService } from '@ngx-translate/core';
import Papa from 'papaparse';
import {
  Observable,
  catchError,
  forkJoin,
  from,
  map,
  mergeMap,
  of,
  switchMap,
  take,
  toArray
} from 'rxjs';

import { EntityInterface } from '@gfe/core/models/entities/entity-visualization-interface';
import { GenericCsvTransformer } from '@gfe/core/models/entities/implementations/generic-csv-transformer';
import {
  EntityDataView,
  EntitySelectorRequestModel,
  PlanFilterOperator
} from '@gfe/core/models/entity/entity-selector-request.model';
import { CommentsV4Service } from '@gfe/core/services/comments/comments-v4.service';
import { CurrentLangService } from '@gfe/core/services/common/current-lang.service';
import { NotificationService } from '@gfe/core/services/notification/notification.service';
import {
  ReferenceNamesService,
  ReferenceResolutionContext
} from '@gfe/core/services/reference-names/reference-names.service';
import { SelectorsService } from '@gfe/core/services/selectors/selectors.service';
import { UserModelsRegistryService } from '@gfe/core/services/user-models-registry/user-models-registry.service';
import { selectCommentableEntityTypes } from '@gfe/core/store/comments-config/comments-config.selectors';
import { selectAvailableTransformers } from '@gfe/core/store/dataspace/dataspace.selectors';
import { ExportPreferences } from '@gfe/core/store/user-preferences/user-preferences.model';
import { selectExportPreferences } from '@gfe/core/store/user-preferences/user-preferences.selectors';
import { getFormatedDateTimeFromDate } from '@gfe/core/utils/date-utils';
import { downloadBlob } from '@gfe/core/utils/download.utils';
import { chunk, isNil } from '@gfe/core/utils/shared-utils';
import { TableColumn } from '@gfe/shared/components/table/table.model';
import { formatCellValueToString, getCellValue } from '@gfe/shared/utils/table-cell.utils';

import { collectReferenceUidsByType as collectTableReferenceUidsByType } from './entity-reference-names.util';
import {
  CommentableRow,
  ExportedComment,
  commentEntityType,
  commentSourcesOf,
  commentUidsByType,
  commentsCellValue,
  exportedComments,
  exportedCommentsKey,
  indexConversations
} from './export-comments.util';
import {
  augmentRowsWithDurationHms,
  buildDurationExportColumns,
  detectDurationColumns,
  durationHmsCellValue,
  durationHmsColumnKeys
} from './export-duration-columns.util';
import {
  ReferenceColumn,
  augmentRowsWithReferenceNames,
  buildReferenceExportColumns,
  collectReferenceUidsByType,
  referenceNameColumnKeys
} from './export-reference-columns.util';
import {
  ExportPlanColumn,
  buildTableExportPlan,
  exportReferenceColumns,
  referenceNamesCellValue
} from './export-table-reference-columns.util';

export interface ExportState {
  isExporting: boolean;
  progress: number;
  total: number;
}

const MAX_EXPORT_ITEMS = 10000;
const EXPORT_BATCH_SIZE = 100;
/** Header marker of a synthetic clock-formatted duration column. */
const HMS_SUFFIX = 'HH:MM:SS';
const COMMENTS_BATCH_SIZE = 500;
const COMMENTS_CONCURRENCY = 4;

@Injectable()
export class DownloadEntitiesDataService<T> {
  private readonly translate = inject(TranslateService);
  private readonly notificationService = inject(NotificationService);
  private readonly selectorsService = inject(SelectorsService);
  private readonly userModelsRegistryService = inject(UserModelsRegistryService);
  private readonly referenceNamesService = inject(ReferenceNamesService);
  private readonly store = inject(Store);
  private readonly currentLangService = inject(CurrentLangService);
  private allTransformers = this.store.selectSignal(selectAvailableTransformers);
  private exportPreferences = this.store.selectSignal(selectExportPreferences);
  private readonly commentsService = inject(CommentsV4Service);
  private readonly commentableTypes = this.store.selectSignal(selectCommentableEntityTypes);

  readonly exportState = signal<ExportState>({
    isExporting: false,
    progress: 0,
    total: 0
  });

  downloadData(
    entityType: string,
    type: 'json' | 'csv',
    modelName?: string,
    lastRequest?: EntitySelectorRequestModel,
    detailEntityTypes: string[] = []
  ) {
    // A raw export writes the entity as-is, so it must never inherit the table's summary
    // projection — the file would be missing fields with nothing to signal it.
    const dataSource$ = this.fetchAllFilteredDataRaw(modelName, lastRequest, 'FULL');

    dataSource$
      .pipe(
        take(1),
        switchMap((data) => {
          const rows = type === 'csv' ? this.expandCustomDataFields(data) : data;
          if (rows.length === 0) return of({ empty: true } as const);
          if (type === 'json') {
            return this.generateJson$(
              rows,
              entityType,
              detailEntityTypes,
              lastRequest
            ).pipe(map((content) => ({ content, count: rows.length })));
          }
          return this.generateCsv$(rows, entityType, modelName, lastRequest).pipe(
            map((content) => ({ content, count: rows.length }))
          );
        }),
        catchError((err) => {
          console.error('Error exporting data:', err);
          this.notifyExportError();
          this.resetExportState();
          return of(null);
        })
      )
      .subscribe((result) => {
        try {
          if (isNil(result)) return;
          if ('empty' in result) {
            this.notifyNoData();
            return;
          }
          const filename = this.generateExportFilename(entityType, type, modelName);
          this.downloadFileContent(result.content, filename, result.count);
        } catch (err) {
          console.error('Error generating file:', err);
          this.notifyExportError();
        } finally {
          this.resetExportState();
        }
      });
  }

  private generateJson$(
    data: T[],
    entityType: string,
    detailEntityTypes: string[],
    lastRequest?: EntitySelectorRequestModel
  ): Observable<string> {
    const rows = data as unknown as Array<Record<string, unknown>>;
    return this.resolveComments$(
      entityType,
      detailEntityTypes,
      () =>
        this.commentableRawRows(rows, lastRequest?.entityTypeVersion, detailEntityTypes),
      lastRequest
    ).pipe(
      map((comments) => {
        if (isNil(comments)) return JSON.stringify(rows, null, 2);
        const key = exportedCommentsKey((field) => rows.some((row) => field in row));
        const content = rows.map((row, index) => ({ ...row, [key]: comments[index] }));
        return JSON.stringify(content, null, 2);
      })
    );
  }

  /**
   * Builds the raw-transformer CSV, resolving referenced entity names only when
   * the "include reference name columns" preference is on (and there are
   * references to resolve), and comments only when the "include comments" one is.
   * Otherwise it stays fully synchronous, leaving the default export
   * byte-identical and issuing no network request.
   */
  private generateCsv$(
    data: T[],
    entityType: string,
    modelName?: string,
    lastRequest?: EntitySelectorRequestModel
  ): Observable<string> {
    const transformer = this.allTransformers().find(
      (transf) =>
        transf.transformerInfo.dataModels[0].metadata.entityTypeVersion === modelName
    );
    const refCols: ReferenceColumn[] = isNil(transformer)
      ? []
      : new GenericCsvTransformer(transformer.transformerInfo).referenceColumns();
    const prefs = this.exportPreferences();
    const rows = data as unknown as Array<Record<string, unknown>>;

    // A transformer's CSV model rows are not entity-shaped, so they carry no
    // detail entities: only a row's own comments can be found.
    const comments$ = this.resolveComments$(
      entityType,
      [],
      () => this.commentableRawRows(rows, undefined, []),
      lastRequest
    );

    return forkJoin([
      this.resolveRawReferenceNames$(refCols, rows, prefs, lastRequest),
      comments$
    ]).pipe(
      map(([namesByType, comments]) =>
        this.generateCSVFromRawData(
          rows,
          entityType,
          modelName,
          namesByType,
          refCols,
          prefs,
          comments
        )
      )
    );
  }

  private resolveRawReferenceNames$(
    refCols: ReferenceColumn[],
    rows: Array<Record<string, unknown>>,
    prefs: ExportPreferences,
    lastRequest?: EntitySelectorRequestModel
  ): Observable<Map<string, Map<string, string>>> {
    const empty = new Map<string, Map<string, string>>();
    if (prefs.includeName !== true || refCols.length === 0 || isNil(lastRequest)) {
      return of(empty);
    }

    const ctx: ReferenceResolutionContext = { dataSpaceId: lastRequest.dataSpaceId };
    const planId = lastRequest.planFilter?.id;
    if (!isNil(planId)) ctx.planId = planId;

    const uidsByType = collectReferenceUidsByType(refCols, rows);
    if (uidsByType.size === 0) return of(empty);

    const requests = [...uidsByType].map(([entityType, uids]) =>
      this.referenceNamesService
        .resolveNames$(entityType, uids, ctx)
        .pipe(map((names) => [entityType, names] as const))
    );

    return forkJoin(requests).pipe(map((entries) => new Map(entries)));
  }

  /**
   * The commented types an export reads: the row's own type and its detail types,
   * each only when the data space allows comments on it. Null when the comments
   * preference is off or none is commentable: the export then has no comments column.
   */
  private commentScope(
    entityType: string,
    detailEntityTypes: string[]
  ): { rowType: string | null; detailTypes: string[] } | null {
    if (this.exportPreferences().includeComments !== true) return null;
    const commentable = new Set(this.commentableTypes());
    const rowType = commentEntityType(entityType);
    const detailTypes = detailEntityTypes.filter((type) =>
      commentable.has(commentEntityType(type))
    );
    if (!commentable.has(rowType) && detailTypes.length === 0) return null;
    return { rowType: commentable.has(rowType) ? rowType : null, detailTypes };
  }

  /**
   * Each row's comments, its own then its detail entities', or null when the
   * export has no comments column: out of `commentScope`, or no row has a uid to
   * look comments up by. `rows` is only built once comments are wanted.
   */
  private resolveComments$(
    entityType: string,
    detailEntityTypes: string[],
    rows: () => CommentableRow[],
    lastRequest?: EntitySelectorRequestModel
  ): Observable<ExportedComment[][] | null> {
    const scope = this.commentScope(entityType, detailEntityTypes);
    if (isNil(scope) || isNil(lastRequest)) return of(null);

    const sourcesPerRow = rows().map((row) =>
      commentSourcesOf(row, scope.rowType, scope.detailTypes)
    );
    if (sourcesPerRow.every((sources) => sources.length === 0)) return of(null);

    const batches = [...commentUidsByType(sourcesPerRow)].flatMap(([type, uids]) =>
      chunk(uids, COMMENTS_BATCH_SIZE).map((batch) => ({ type, uids: batch }))
    );

    // A NOT_IN plan filter lists data-space rows outside the plan: their comments are
    // not the plan's, so the search must not be scoped to it.
    const planFilter = lastRequest.planFilter;
    const planId =
      planFilter?.operator === PlanFilterOperator.IN ? planFilter.id : undefined;

    return from(batches).pipe(
      mergeMap(
        ({ type, uids }) =>
          this.commentsService.getConversations$({
            entityType: type,
            entityUids: uids,
            dataspaceId: lastRequest.dataSpaceId,
            planId,
            includeResolved: true
          }),
        COMMENTS_CONCURRENCY
      ),
      toArray(),
      map((pages) => {
        const conversations = indexConversations(pages.flat());
        return sourcesPerRow.map((sources) => exportedComments(sources, conversations));
      }),
      // Comments are informational: losing them must not cost the user the export.
      catchError((err) => {
        console.error('Error fetching export comments:', err);
        this.notificationService.warning(
          this.translate.instant('features.entities.export.comments.failed.title'),
          this.translate.instant('features.entities.export.comments.failed.message'),
          5000
        );
        return of(null);
      })
    );
  }

  /**
   * Raw rows as rows comments can be looked up on. Detail entities exist only on
   * the entity model, so the rows are converted to it when there are detail types
   * to read; otherwise a row only offers its own `uid`.
   */
  private commentableRawRows(
    rows: Array<Record<string, unknown>>,
    versionName: string | undefined,
    detailEntityTypes: string[]
  ): CommentableRow[] {
    if (detailEntityTypes.length > 0 && !isNil(versionName)) {
      const entities = this.userModelsRegistryService.convertToEntityInterface(
        rows,
        versionName
      );
      if (entities.length === rows.length) return entities;
    }
    return rows.map((row) => ({
      uid: typeof row['uid'] === 'string' ? row['uid'] : '',
      getDetails: () => []
    }));
  }

  private commentsCell(comments: ExportedComment[], entityType: string): string {
    const lang = this.exportLang();
    return commentsCellValue(comments, commentEntityType(entityType), {
      date: (iso) => getFormatedDateTimeFromDate(new Date(iso), lang),
      resolved: this.translate.instant('features.entities.export.comments.resolved'),
      typeName: (type) => {
        const key = `entities.${type.toLowerCase()}`;
        const translated: string = this.translate.instant(key);
        return translated === key ? type : translated;
      }
    });
  }

  private exportLang(): string {
    return (
      this.currentLangService.currentLang() || this.translate.getFallbackLang() || 'en'
    );
  }

  private fetchAllFilteredDataRaw(
    modelName?: string,
    fetchRequest?: EntitySelectorRequestModel,
    view?: EntityDataView
  ): Observable<T[]> {
    if (isNil(fetchRequest)) {
      return of([]);
    }

    // Copy rather than mutate: this is the caller's live table request, and writing a version
    // or a projection into it would leak into the next page fetch.
    const request: EntitySelectorRequestModel = {
      ...fetchRequest,
      ...(isNil(modelName) ? {} : { entityTypeVersion: modelName }),
      ...(isNil(view) ? {} : { view })
    };

    // Initialize export state
    this.exportState.set({
      isExporting: true,
      progress: 0,
      total: 0
    });

    return this.selectorsService
      .selectAllData$<T>(request, {
        maxItems: MAX_EXPORT_ITEMS,
        batchSize: EXPORT_BATCH_SIZE,
        keepOriginalCase: true,
        onProgress: ({ current, total }) => {
          this.exportState.update((state) => ({
            ...state,
            isExporting: true,
            progress: current,
            total
          }));
        }
      })
      .pipe(
        map((result) => result.items),
        catchError((err) => {
          console.error('Error fetching all filtered raw data:', err);

          // Check if it's a limit exceeded error
          if (err.message?.includes('exceeds maximum allowed')) {
            const match = err.message.match(/Total items \((\d+)\)/);
            const totalItems = match ? parseInt(match[1], 10) : 0;

            this.notificationService.warning(
              this.translate.instant('features.entities.export.limit_exceeded.title'),
              this.translate.instant('features.entities.export.limit_exceeded.message', {
                count: totalItems,
                max: MAX_EXPORT_ITEMS
              }),
              5000
            );
          }

          this.exportState.set({
            isExporting: false,
            progress: 0,
            total: 0
          });
          return of([]);
        })
      );
  }

  private generateExportFilename(
    entityType: string,
    type: 'json' | 'csv',
    transformerName?: string
  ): string {
    const timestamp = new Date().toISOString().split('T')[0]; // YYYY-MM-DD format
    const transformerSuffix = transformerName ? `-${transformerName}` : '';
    return `export-${entityType}${transformerSuffix}-${timestamp}.${type}`;
  }

  downloadDisplayedTableData<U>(
    columns: TableColumn<EntityInterface<U>>[],
    entityType: string,
    lastRequest: EntitySelectorRequestModel,
    detailEntityTypes: string[] = []
  ): void {
    // The table may list a summary projection, which omits the detail entities (a
    // route's `actions`); their comments can only be found on the full rows.
    const needsDetails =
      (this.commentScope(entityType, detailEntityTypes)?.detailTypes.length ?? 0) > 0;
    const dataSource$ = this.fetchAllFilteredDataRaw(
      undefined,
      lastRequest,
      needsDetails ? 'FULL' : undefined
    );
    // Subscribe to data and generate file
    dataSource$
      .pipe(
        take(1),
        // Convert raw API objects into proper EntityInterface instances (e.g. UserRouteV1)
        // so that computed properties and renderHooks work correctly. No auxiliary data:
        // exported cells come from renderHooks and precomputed fields, and referenced names
        // are resolved separately below.
        map((rawRows) =>
          this.userModelsRegistryService.convertToEntityInterface<U>(
            rawRows as unknown as U[],
            lastRequest.entityTypeVersion!
          )
        ),
        // Resolve referenced entity names only when the "include reference name
        // columns" preference is on; otherwise stay synchronous with an empty map,
        // leaving the default export byte-identical and issuing no network request.
        switchMap((entities) =>
          forkJoin([
            this.resolveTableReferenceNames$(columns, entities, lastRequest),
            this.resolveComments$(
              entityType,
              detailEntityTypes,
              () => entities,
              lastRequest
            )
          ]).pipe(map(([namesByType, comments]) => ({ entities, namesByType, comments })))
        ),
        catchError((err) => {
          console.error('Error exporting table data:', err);
          this.notifyExportError();
          this.resetExportState();
          return of(null);
        })
      )
      .subscribe((result) => {
        if (isNil(result)) return;
        const { entities, namesByType, comments } = result;
        if (entities.length === 0) {
          this.notifyNoData();
          return;
        }
        try {
          // Build CSV using the same columns and formatting as the table
          const { headers, dataRows } = this.buildTableCsvData(
            columns,
            entities,
            namesByType,
            comments,
            entityType
          );

          const csv = Papa.unparse(
            { fields: headers, data: dataRows },
            { delimiter: ';', newline: '\r\n' }
          );

          // Generate filename
          const filename = this.generateExportFilename(entityType, 'csv', 'table');

          // Create and download file
          this.downloadFileContent('\ufeff' + csv, filename, entities.length);
        } catch (err) {
          console.error('Error generating file:', err);
          this.notifyExportError();
        } finally {
          this.resetExportState();
        }
      });
  }

  private resolveTableReferenceNames$<U>(
    columns: TableColumn<EntityInterface<U>>[],
    entities: EntityInterface<U>[],
    lastRequest: EntitySelectorRequestModel
  ): Observable<Map<string, Map<string, string>>> {
    const empty = new Map<string, Map<string, string>>();
    if (this.exportPreferences().includeName !== true || entities.length === 0) {
      return of(empty);
    }

    const refCols = exportReferenceColumns(columns);
    if (refCols.length === 0) return of(empty);

    const uidsByType = collectTableReferenceUidsByType(refCols, entities);
    if (uidsByType.size === 0) return of(empty);

    const ctx: ReferenceResolutionContext = { dataSpaceId: lastRequest.dataSpaceId };
    const planId = lastRequest.planFilter?.id;
    if (!isNil(planId)) ctx.planId = planId;

    const requests = [...uidsByType].map(([type, uids]) =>
      this.referenceNamesService
        .resolveNames$(type, uids, ctx)
        .pipe(map((names) => [type, names] as const))
    );

    return forkJoin(requests).pipe(map((entries) => new Map(entries)));
  }

  private buildTableCsvData<U>(
    columns: TableColumn<EntityInterface<U>>[],
    rows: EntityInterface<U>[],
    namesByType: Map<string, Map<string, string>>,
    comments: ExportedComment[][] | null,
    entityType: string
  ): { headers: string[]; dataRows: string[][] } {
    const currentLang = this.exportLang();
    const nameSuffix = this.translate.instant('common.name');
    const plan = buildTableExportPlan(columns, this.exportPreferences());

    // Use display label if available, otherwise fall back to the key; a synthetic
    // column reuses its source column's label with a "(Name)" / "(HH:MM:SS)"
    // suffix so the two stay distinguishable when both are exported.
    const headerFor = (item: ExportPlanColumn<EntityInterface<U>>): string => {
      const base = String(item.column.label ?? item.column.key);
      if (item.kind === 'name') return `${base} (${nameSuffix})`;
      if (item.kind === 'duration-hms') return `${base} (${HMS_SUFFIX})`;
      return base;
    };
    const headers = plan.map(headerFor);

    const dataRows = rows.map((row) =>
      plan.map((item) => {
        if (item.kind === 'name') {
          return referenceNamesCellValue(
            row,
            item.column,
            namesByType.get(item.entityType)
          );
        }
        if (item.kind === 'duration-hms') {
          return durationHmsCellValue(row, item.column);
        }
        const value = getCellValue(row, item.column);
        return formatCellValueToString(value, item.column.type, currentLang);
      })
    );

    if (!isNil(comments)) {
      headers.push(this.translate.instant('features.entities.export.comments.column'));
      dataRows.forEach((dataRow, index) =>
        dataRow.push(this.commentsCell(comments[index], entityType))
      );
    }

    return { headers, dataRows };
  }

  private expandCustomDataFields(data: T[]): T[] {
    // Collect all unique label keys across all rows
    const allLabelKeys = new Set<string>();
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    data.forEach((row: any) => {
      if (row.custom_data?.labels) {
        Object.keys(row.custom_data.labels).forEach((key) => allLabelKeys.add(key));
      }
    });

    // Transform each row
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    return data.map((row: any) => {
      const { custom_data, ...rest } = row;
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      const expanded: any = { ...rest };

      if (custom_data) {
        // Add tags column (comma-separated)
        expanded.tags =
          custom_data.tags && Array.isArray(custom_data.tags)
            ? custom_data.tags.join(',')
            : '';

        // Add other_data column (stringified JSON)
        expanded.other_data =
          custom_data.other_data !== undefined && custom_data.other_data !== null
            ? JSON.stringify(custom_data.other_data)
            : '';

        // Add cl_* columns for each label
        allLabelKeys.forEach((labelKey) => {
          expanded[`cl_${labelKey}`] = custom_data.labels?.[labelKey] ?? '';
        });
      } else {
        allLabelKeys.forEach((labelKey) => {
          expanded[`cl_${labelKey}`] = '';
        });
      }

      return expanded;
    });
  }

  private generateCSVFromRawData(
    data: Array<Record<string, unknown>>,
    entityType: string,
    modelName: string | undefined,
    namesByType: Map<string, Map<string, string>>,
    refCols: ReferenceColumn[],
    prefs: ExportPreferences,
    comments: ExportedComment[][] | null
  ): string {
    const transformer = this.allTransformers().find(
      (transf) =>
        transf.transformerInfo.dataModels[0].metadata.entityTypeVersion === modelName
    );
    let colsInOrder = undefined;
    let schemaDurationFields: string[] = [];
    if (!isNil(transformer)) {
      const csvTransformer = new GenericCsvTransformer(transformer.transformerInfo);
      colsInOrder = csvTransformer.colsInOrder();
      schemaDurationFields = csvTransformer.durationColumns();
    }
    const allColumns = Array.from(new Set(data.flatMap((row) => Object.keys(row))));
    // A schema-declared duration is a duration column even when this page holds no
    // value for it; a repeatable one (`patternProperties`) is only recognisable
    // from its values.
    const durationFields = Array.from(
      new Set([...schemaDurationFields, ...detectDurationColumns(allColumns, data)])
    );

    // Synthetic-column keys are derived from the pre-augmentation columns, then
    // used both to augment the rows and to place the columns, so the two agree.
    const includeName = prefs.includeName === true;
    const nameKeyByField = referenceNameColumnKeys(refCols, allColumns);
    const hmsKeyByField = durationHmsColumnKeys(durationFields, [
      ...allColumns,
      ...nameKeyByField.values()
    ]);
    let rows = includeName
      ? augmentRowsWithReferenceNames(data, refCols, namesByType, nameKeyByField)
      : data;
    if (prefs.includeDurationHms === true) {
      rows = augmentRowsWithDurationHms(rows, durationFields, hmsKeyByField);
    }
    const commentsKey = exportedCommentsKey((field) => allColumns.includes(field));
    if (!isNil(comments)) {
      rows = rows.map((row, index) => ({
        ...row,
        [commentsKey]: this.commentsCell(comments[index], entityType)
      }));
    }
    const commentsColumn = isNil(comments) ? [] : [commentsKey];

    // Build the final column order: specified columns first, then remaining ones
    let finalColumns: string[] | undefined;

    if (!isNil(colsInOrder)) {
      const colsSet = new Set(colsInOrder);
      const remainingCols = allColumns.filter((col) => !colsSet.has(col));

      // Separate custom_data-derived columns from other columns
      const customDataCols = remainingCols.filter(
        (col) => col === 'tags' || col === 'other_data' || col.startsWith('cl_')
      );
      const otherCols = remainingCols.filter(
        (col) => col !== 'tags' && col !== 'other_data' && !col.startsWith('cl_')
      );

      // Order: schema columns, other columns, then custom_data columns
      // Within custom_data: tags, other_data, then cl_* (alphabetically)
      const sortedCustomDataCols = [
        ...customDataCols.filter((col) => col === 'tags'),
        ...customDataCols.filter((col) => col === 'other_data'),
        ...customDataCols.filter((col) => col.startsWith('cl_')).sort()
      ];

      finalColumns = [
        ...buildDurationExportColumns(
          buildReferenceExportColumns(
            [...colsInOrder, ...otherCols, ...sortedCustomDataCols],
            refCols,
            prefs,
            nameKeyByField
          ),
          durationFields,
          prefs,
          hmsKeyByField
        ),
        ...commentsColumn
      ];
    } else {
      // With no transformer there is no reference metadata; only materialise an
      // explicit list when the preferences actually change the columns, so the
      // default export stays byte-identical (undefined => Papa infers keys).
      const adjusted = buildDurationExportColumns(
        buildReferenceExportColumns(allColumns, refCols, prefs, nameKeyByField),
        durationFields,
        prefs,
        hmsKeyByField
      );
      finalColumns =
        adjusted === allColumns && commentsColumn.length === 0
          ? undefined
          : [...adjusted, ...commentsColumn];
    }

    const currentLang = this.exportLang();

    // Create a number formatter for the current locale
    const numberFormatter = new Intl.NumberFormat(currentLang, {
      useGrouping: false,
      maximumFractionDigits: 20
    });

    // Transform numeric values based on locale
    const transformedData = rows.map((row) => {
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      const newRow: any = {};
      for (const [key, value] of Object.entries(row)) {
        if (typeof value === 'number') {
          newRow[key] = numberFormatter.format(value);
        } else {
          newRow[key] = value;
        }
      }
      return newRow;
    });

    const csv = Papa.unparse(transformedData, {
      header: true,
      delimiter: ';',
      newline: '\r\n',
      columns: !isNil(finalColumns) ? finalColumns : undefined
    });

    return '\ufeff' + csv;
  }

  private downloadFileContent(content: string, filename: string, count: number): void {
    try {
      downloadBlob(new Blob([content], { type: 'application/octet-stream' }), filename);

      // Show success notification
      this.notificationService.success(
        this.translate.instant('features.entities.export.success.title'),
        this.translate.instant('features.entities.export.success.message', {
          count,
          filename
        }),
        3000
      );
    } catch (err) {
      console.error('Error generating file:', err);
      this.notifyExportError();
    } finally {
      // Reset export state
      this.resetExportState();
    }
  }

  private notifyExportError(): void {
    this.notificationService.error(
      this.translate.instant('features.entities.export.error.title'),
      this.translate.instant('features.entities.export.error.message')
    );
  }

  private notifyNoData(): void {
    this.notificationService.warning(
      this.translate.instant('features.entities.export.no_data.title'),
      this.translate.instant('features.entities.export.no_data.message'),
      3000
    );
  }

  private resetExportState(): void {
    this.exportState.set({ isExporting: false, progress: 0, total: 0 });
  }
}
